import React, { useEffect } from 'react';
import PlanetScene from './components/PlanetScene';
import Hud from './components/Hud';
import LiveChatDock from './components/LiveChatDock';
import MeetDock from './components/MeetDock';
import AvatarChatDock from './components/AvatarChatDock';
import GuestChatDock from './components/GuestChatDock';
import ShopDock from './components/ShopDock';
import ListeningDock from './components/ListeningDock';
import CallRing from './components/CallRing';
import PerformToggle from './components/PerformToggle';
import RoutesPanel from './components/RoutesPanel';
import NpcPlacePanel from './components/NpcPlacePanel';
import CarSizePanel from './components/CarSizePanel';
import PerformerPanel from './components/PerformerPanel';
import GuardPanel from './components/GuardPanel';
import AdminPanel from './components/AdminPanel';
import PlaceablesPanel from './components/PlaceablesPanel';
import PlacementPanel from './components/PlacementPanel';
import PlaquePanel from './components/PlaquePanel';
import DjBillboardHost from './components/DjBillboardHost';
import Loader from './components/Loader';
import IntroTitles from './components/IntroTitles';
import AppearAs from './components/AppearAs';
import CharacterSelect from './components/CharacterSelect';
import SpawnAsAvatar from './components/SpawnAsAvatar';
import ChatBar from './components/ChatBar';
import SceneDock from './components/SceneDock';
import SceneStaging from './components/SceneStaging';
import RideOverlay from './components/RideOverlay';
import WalkGuide from './components/WalkGuide';
import PremiereMoment from './components/PremiereMoment';
import GuestClaim from './components/GuestClaim';
import VRButton from './components/VRButton';
import { VRHide, VRSafe } from './components/VRStage';
import AccessBoot from './components/AccessBoot';
import AccessRefusal from './components/AccessRefusal';
import StoryPanel from './components/StoryPanel';
import Phone from './components/Phone';
import RoomSound from './components/RoomSound';
import RecordPlaquePanel from './components/RecordPlaquePanel';
import RecordCardsPanel from './components/RecordCardsPanel';
import BlockPanel from './components/BlockPanel';
import VenuePlacePanel from './components/VenuePlacePanel';
import PortalPanel from './components/PortalPanel';
import RoomConfigs from './components/RoomConfigs';
import InteriorResidentPanel from './components/InteriorResidentPanel';
import { IS_GUEST, API_BASE } from './config';
import { ROOMS } from './rooms';
import { useWorldStore } from './state/worldStore';
import { onNowPlaying } from './services/audioEngine';
import { connectWorld, disconnectWorld } from './services/worldSocket';

export default function App() {
  const setNowPlaying = useWorldStore((s) => s.setNowPlaying);

  // ?fresh=1 — one-shot clean slate: wipe every locally-saved tuning override
  // (camera configs, panel placements) so the world boots purely from
  // rooms.js + manifests. The escape hatch when saved states conflict.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('fresh')) {
      const wiped = Object.keys(localStorage).filter((k) => k.startsWith('orbit:') || k.startsWith('videoPanel:') || k.startsWith('bb:') || k.startsWith('route:') || k.startsWith('npcpos:'));
      wiped.forEach((k) => localStorage.removeItem(k));
      console.log(`[world] fresh boot — cleared ${wiped.length} saved override(s):`, wiped);
    }
  }, []);

  // ?enter=<roomId> — jump straight into an interior (dev/deep-link; how a
  // freshly baked scanned room is walked before it has a city anchor).
  //
  // ?enter=moment-<postId> — a BAKED MOMENT SCENE (post → Gemini 360 →
  // Marble): rooms as DATA. The manifest comes from the API (which proxies
  // every splat asset — the bucket blocks direct browser fetches), so a
  // runtime ROOMS entry is registered here and the normal interior machinery
  // takes over. Free to walk, forever — that's the bake's whole economics.
  //
  // ?meet=<CODE> — a meet invite link: same walk-in, destination fixed to the
  // scanned studio (the meet venue), and MeetDock picks the code up itself.
  // The join stays a button in the dock — never auto-join a live call.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const key = params.get('enter') || (params.get('meet') ? 'room-studio' : null);
    if (!key) return undefined;
    const moment = key.match(/^moment-(\d+)$/);
    if (moment && !ROOMS[key]) {
      ROOMS[key] = {
        name: 'A moment',
        manifest: `${API_BASE}/api/posts/${moment[1]}/scene-manifest`,
        accent: '#ffb25e',
        // Standing eye-height at the pano's capture point; the manifest's own
        // spawn (if Marble semantics provided one) wins once the splat loads.
        spawn: { pos: [0, 1.5, 0.4], look: [0, 1.4, -1.2] },
        orbit: { min: 0.01, max: 2000, minPolar: 0, maxPolar: 3.14 },
      };
    }
    if (!ROOMS[key]) return undefined;
    const t = setTimeout(() => useWorldStore.getState().enterInterior(key), 400);
    return () => clearTimeout(t);
  }, []);

  // ARRIVING FROM A PUSH NOTIFICATION (?listen=CODE).
  //
  // Mounted here rather than in ListeningDock because the dock only renders in
  // a room that HAS seats — and somebody who just tapped a push is standing
  // wherever the world spawned them, which is the street. The invite would have
  // silently done nothing at exactly the moment it mattered most.
  //
  // Filed into the Calls log, so it behaves identically whether it arrived over
  // the socket or through a notification: one row, one button, one place to
  // look. The room is fetched for its ZONE — without it "Take seat" could join
  // you to a room and leave you standing outside it.
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('listen');
    if (!code) return;
    let dead = false;
    import('./services/listening')
      .then((LR) => LR.roomByCode(code.toUpperCase()))
      .then((room) => {
        if (dead || !room) return;
        useWorldStore.getState().noteListeningInvite({
          code: room.code,
          zone: room.zone,
          from: { name: 'A listening room' },
          at: Date.now(),
        });
      })
      .catch(() => { /* a closed room is a normal answer, not an error */ });
    return () => { dead = true; };
  }, []);

  useEffect(() => {
    // Feed live now-playing into the world store (drives ticker + modulation pulls).
    const off = onNowPlaying((np) => setNowPlaying(np));
    return off;
  }, [setNowPlaying]);

  // Multiplayer presence + shared world clock: open the /world socket once the
  // player is actually IN the world. It follows their area and feeds peers into
  // presences.
  //
  // NOT ON MOUNT. The socket announces presence the instant it connects — the
  // server counts every connected socket in `online`, and the client emits
  // `enter` from its own connect handler, which puts the newcomer in the room
  // roster and fires `peer_enter` at everyone already there. Opening the page
  // was therefore enough to be counted, rostered, and drawn as a figure in
  // somebody else's street, all while still sitting on the loading screen.
  //
  // Three things that cost: the online count read high, every real player paid
  // draw calls rendering peers who were not there, and — worst — a MEMBER
  // reaching this point marked their avatar as embodied, which tells every
  // client to hide that avatar's autonomous NPC. Someone who opened the tab and
  // wandered off silently removed their artist from the world for everyone.
  //
  // Safe to defer: serverNow() falls back to the device clock until the offset
  // arrives, and the correction lands as the world opens.
  const entered = useWorldStore((s) => s.entered);
  useEffect(() => {
    if (!entered) return undefined;
    connectWorld();
    return () => disconnectWorld();
  }, [entered]);

  // Warm the splat renderer once the player has landed and the city has had a
  // few seconds to itself. Spark is a 4.9MB chunk that only splat rooms need —
  // kept off the first load (SplatWorld.loadSpark) and fetched here in idle time,
  // so the first splat room still opens without waiting on it.
  const arrivedAt = useWorldStore((s) => s.arrivedAt);
  useEffect(() => {
    if (!arrivedAt) return undefined;
    let idle = null;
    const t = setTimeout(() => {
      const go = () => import('./components/SplatWorld').then((m) => m.prefetchSpark()).catch(() => {});
      idle = window.requestIdleCallback ? window.requestIdleCallback(go, { timeout: 10000 }) : (go(), null);
    }, 6000);
    return () => { clearTimeout(t); if (idle && window.cancelIdleCallback) window.cancelIdleCallback(idle); };
  }, [arrivedAt]);

  return (
    <>
      <PlanetScene />
      {/* EVERYTHING FLAT GOES AWAY IN SPLIT VIEW. A DOM overlay spans both eyes
          as a single element, so neither eye gets a complete copy and the brain
          has nothing to fuse — it reads as an object at an impossible depth
          across the seam, which is the disorientation rather than clutter.
          The essentials are re-issued per eye below (VRSafe). */}
      <VRHide>
      <Hud />
      {/* Members pick which of their avatars they appear as (name + headshot). */}
      {!IS_GUEST && <AppearAs />}
      <CharacterSelect />
      {/* Embodiment: spawn a member at their avatar's world spot (if it lives here). */}
      {!IS_GUEST && <SpawnAsAvatar />}
      {/* Proximity chat with nearby peers (guests + members). */}
      <ChatBar />

      {/* Stage One acts — directed conversation in the room it belongs to.
          Admin-gated inside the component while the happy path settles. */}
      <SceneDock />
      {/* Spor1's ride, DOM half: the kerbside pickup, the landmark reveal
          cards, and the "with Spor1" chip. The camera half is RideSequence
          inside the Canvas. */}
      <RideOverlay />
      {/* The walk's guardrail (§4.4): studio waypoint chip + Lyra's stall/stray
          nudge. Getting lost between the salon and the studio is the intro's
          most likely churn point — this is the fence around it. */}
      <WalkGuide />
      {/* The premiere (§4.6): armed by release consent in the session, fired by
          stepping outside — their record takes the street's audio, ON AIR card,
          and the drop quest's goto reports itself. */}
      <PremiereMoment />
      {/* The salon signup (guest → member, right before purchase): the ride's
          answers become the account and the avatar shell, then the world
          reboots as a member standing in the salon. */}
      <GuestClaim />
      {/* Blocking, not dialogue: who walks where once you sit down, and the one
          control that always ends a set piece. Outside the Canvas so the way out
          cannot be behind you. */}
      <SceneStaging />
      {/* Which rung the viewer is on, and what a shut door says about it. Both
          outside the Canvas: the answer is needed before anything is drawn, and
          the refusal must be readable wherever they happen to be looking. */}
      <AccessBoot />
      <AccessRefusal />
      <StoryPanel />
      {/* The phone (treatment §3) — the only permitted non-diegetic-looking
          surface, and the way into the acts that have no building: Lyra's call
          rings here, and you hail the ride from it. Never modal — it raises and
          lowers while the world keeps running. */}
      {/* A room with its own sound takes the station's place while you are in it. */}
      <RoomSound />
      <Phone />
      {/* The studio's wall of gold records — click a plaque to set its artwork
          or put the record out. The diegetic home for release. */}
      <RecordPlaquePanel />
      <RecordCardsPanel />
      <BlockPanel />
      {/* Venue anchor brush controls — the in-scene half is VenuePlacer. */}
      <VenuePlacePanel />
      <PortalPanel />
      <RoomConfigs />
      <InteriorResidentPanel />
      {/* Live Anam call dock — DOM overlay; the stream itself lands on the
          room's 3D video card via worldStore.liveVideoEl. */}
      <LiveChatDock />
      {/* Live MEET dock (?meet=CODE) — a real person (the artist, running the
          web studio collab with their X2 avatar) lands on the scanned studio's
          panel via the same liveVideoEl seam. */}
      <MeetDock />
      {/* Voice chat with a clicked 3D resident (push-to-talk; the character
          body-animates while speaking via talkBridge). */}
      <AvatarChatDock />
      {/* Logged-out visitors: email-gated text chat via /api/chat/guest.
          (AvatarChatDock above is voice + login-only; GuestChatDock is guest-only.) */}
      <GuestChatDock />
      {/* The record shop counter — the clerk you talk to, whose picks land on
          the sleeve wall behind him rather than in this panel. */}
      <ShopDock />
      {/* Open a listening room in the room you are standing in — the FRONT DOOR
          for the feature (Khari: the phone "implies you'd be planning it instead
          of initiating it... most first time users will look" in the room).
          Self-hides where there is no furniture to sit on. */}
      <ListeningDock />
      {/* Somebody calling you — over the socket while you are in the city, or
          from a notification link that lands as ?call=CODE. */}
      <CallRing />
      {/* SPIKE (?face=1): webcam → live head pose on the resident (faceBridge). */}
      <PerformToggle />
      {/* World tuning tools — all driven by one hub (AdminPanel ⚙, admins only).
          Each self-shows when its adminTool is active; only one at a time. */}
      <AdminPanel />
      <PlaceablesPanel />
      <PlacementPanel />
      <PlaquePanel />
      <RoutesPanel />
      <NpcPlacePanel />
      <CarSizePanel />
      <PerformerPanel />
      <GuardPanel />
      {/* Radio DJ video-host cameo — the DJ speaks on the big billboards for
          dynamic moments (hidden stream video lives here; ?dj shows chrome). */}
      <DjBillboardHost />
      {/* Cinematic titling over the street fly-through (Location · time · Welcome)
          + Skip button — driven by introBridge. */}
      <IntroTitles />
      {/* Branded loading→enter gate; the Enter click starts audio + the fly-through. */}
      <Loader />
      </VRHide>

      {/* THE ONLY UI THAT SURVIVES A HEADSET, drawn once per eye so each has a
          complete copy at the same place. Outside VRHide on purpose: the way out
          of VR must never be the thing VR hides. DOM rather than in-canvas
          because the iOS motion permission has to come from a real tap. */}
      <VRSafe><VRButton /></VRSafe>
    </>
  );
}